<script setup lang="ts">
import type { ReelItemProperty } from './types';
import type { Property, ShortVideoProperty } from '~/core/entities/property';
import type { ShortsShareChannel } from '~/types/mixpanel';
import type { Ref } from 'vue';

import { cn } from '@/utils';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { useShortsContactModalVideoPause } from '~/composables/shorts';
import { usePropertyHeroShare } from '~/composables/usePropertyHeroShare';
import { appendUtmParamsToUrl, REELS_SHARE_UTM_PARAMS } from '~~/utils';
import { toRef } from 'vue';

import { FavoriteButton } from '~/components/FavoriteButton';
import { GlassButton } from '~/components/GlassButton';
import { ShareContent } from '~/components/ShareContent';

import IconMaterialSymbolsLightStar from '~icons/material-symbols-light/star';

import {
	buildShareShortsClickedPayload,
	buildShortsSharePropertyPayload
} from './shortsMixpanelPayloads';
import { useShortsEntryContext } from './useShortsEntryContext';

interface ReelActionButtonsProps {
	property: ReelItemProperty;
	isFavorite: boolean;
	isFavoriteDataValid: boolean;
	isFavoriteMainPulsing: boolean;
	isFavoriteBackgroundPulsing: boolean;
	isShareAnimating: boolean;
	/** Body text for native share (mobile), e.g. "Confira este imóvel por …". */
	sharePromoText?: string;
	mailSubject?: string;
	shortsIndex?: number;
}

const props = withDefaults(defineProps<ReelActionButtonsProps>(), {
	sharePromoText: '',
	mailSubject: 'Leia mais sobre este imóvel'
});

const emit = defineEmits<{
	(e: 'toggle-favorite'): void;
	(e: 'share'): void;
}>();

const shortsEntry = useShortsEntryContext();

const propertyRef = toRef(props, 'property') as Ref<
	Property | ShortVideoProperty | null
>;

const { ORIGIN } = useRuntimeConfig().public;

const shareUrlOverride = computed(() =>
	appendUtmParamsToUrl(`${ORIGIN}/shorts/${props.property.commercialId}`, {
		...REELS_SHARE_UTM_PARAMS
	})
);

const shareTextOverride = computed(() =>
	props.sharePromoText.trim() ? props.sharePromoText : undefined
);

const shareAnalyticsExtras = computed(() =>
	shortsEntry.value
		? buildShortsSharePropertyPayload(props.property, shortsEntry.value)
		: undefined
);

const {
	showShareContent,
	shareContentRef,
	shareContentButton,
	shareUrl,
	toggleShareContent,
	shareMobile,
	getMobileShareChannel,
	handleShareClicked
} = usePropertyHeroShare({
	property: propertyRef,
	shareUrlOverride,
	shareTextOverride,
	onAfterFallbackCopy: () => emit('share'),
	getShareTrackExtras: () => shareAnalyticsExtras.value
});

const { notifyContactModalOpenChange: notifyShareOverlayPauseChange } =
	useShortsContactModalVideoPause();

/** Desktop share dropdown uses `lg:block`; keep breakpoint in sync with Tailwind. */
const isDesktop = useBreakpoints(breakpointsTailwind).greater('lg');

watch(showShareContent, isOpen => {
	notifyShareOverlayPauseChange(isOpen);
});

// Desktop dropdown is hidden below `lg`; clear stale open state so video pause does not stick.
watch(isDesktop, isNowDesktop => {
	if (!isNowDesktop && showShareContent.value) {
		showShareContent.value = false;
	}
});

const { trackShareShortsClicked } = useEvents();

function trackShareClick(shareChannel: ShortsShareChannel) {
	if (props.shortsIndex === undefined) return;
	trackShareShortsClicked(
		buildShareShortsClickedPayload({
			commercialId: props.property.commercialId,
			shareChannel,
			shortsIndex: props.shortsIndex
		})
	);
}

function handleDesktopShareClick() {
	if (!showShareContent.value) trackShareClick('share_menu');
	toggleShareContent();
}

async function shareMobileWithVideoPause() {
	trackShareClick(getMobileShareChannel());
	notifyShareOverlayPauseChange(true);
	try {
		await shareMobile();
	} finally {
		notifyShareOverlayPauseChange(false);
	}
}

onBeforeUnmount(() => {
	if (showShareContent.value) {
		notifyShareOverlayPauseChange(false);
	}
});
</script>

<template>
	<div class="flex flex-col gap-3">
		<div class="relative">
			<IconMaterialSymbolsLightStar
				:class="[
					'absolute left-1/2 top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 transition-all duration-300',
					isFavorite ? 'text-white/40' : 'text-white/20',
					isFavoriteBackgroundPulsing && isFavoriteDataValid
						? 'scale-[2] opacity-60'
						: 'scale-100 opacity-0'
				]"
			/>
			<FavoriteButton
				v-if="isFavoriteDataValid"
				:is-favorite="isFavorite"
				:property-id="property.id"
				:commercial-id="property.commercialId"
				:class="
					cn(
						'relative z-10 size-12 rounded-full border backdrop-blur-[6px] transition-all duration-300 ease-out hover:scale-105 focus-visible:outline-none',
						isFavorite
							? 'border-transparent bg-beige text-primary hover:border-transparent hover:bg-beige-light-2 focus:border-transparent focus:bg-beige-light-2 focus-visible:border-transparent focus-visible:bg-beige-light-2'
							: 'border-white/10 bg-[rgba(102,102,102,0.30)] text-white hover:border-white/25 hover:bg-[rgba(102,102,102,0.5)] focus:border-white/25 focus:bg-[rgba(102,102,102,0.5)] focus-visible:border-white/25 focus-visible:bg-[rgba(102,102,102,0.5)]',
						isFavoriteMainPulsing && 'scale-125'
					)
				"
				:icon-class="cn('size-7!', isFavorite ? 'text-primary' : 'text-white')"
				@toggle="emit('toggle-favorite')"
			/>
		</div>

		<!-- Mobile: native share sheet (same idea as PropertyHeroShareControls) -->
		<GlassButton
			variant="action"
			size="md"
			class="lg:hidden"
			:class="isShareAnimating && 'scale-125 bg-white/30'"
			aria-label="Compartilhar"
			@click.stop="shareMobileWithVideoPause"
		>
			<IconPilarSend48Degrees
				:class="[
					'size-5 transition-colors',
					isShareAnimating ? 'text-white' : 'text-white/70'
				]"
			/>
		</GlassButton>

		<!-- Desktop: dropdown with share options -->
		<div class="relative hidden lg:block">
			<GlassButton
				ref="shareContentButton"
				variant="action"
				size="md"
				:class="isShareAnimating && 'scale-125 bg-white/30'"
				aria-label="Compartilhar"
				@click.stop="handleDesktopShareClick"
			>
				<IconPilarSend48Degrees
					:class="[
						'size-5 transition-colors',
						isShareAnimating ? 'text-white' : 'text-white/70'
					]"
				/>
			</GlassButton>

			<div
				v-show="showShareContent"
				ref="shareContentRef"
				class="absolute right-0 bottom-full z-40 mb-2 w-[180px] border border-white-dark-3 bg-white p-[0.7rem_1rem]"
			>
				<ShareContent
					v-model="showShareContent"
					variant="dropdown"
					:mail-subject="mailSubject"
					:content-to-share="shareUrl"
					:whatsapp-promo-text="sharePromoText"
					:share-analytics-extras="shareAnalyticsExtras"
					show-labels
					@share-clicked="handleShareClicked"
				/>
			</div>
		</div>
	</div>
</template>
